import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { IconDownload, IconLeaf } from '@tabler/icons-react';

const DEFRA_FACTOR = 2.68; // kg CO2 per litre of diesel

export default function CarbonReport() {
  const { orgId, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const tanksSnap = await getDocs(collection(db, 'organisations', orgId, 'dervTanks'));
        const tanks = scopeToSite(tanksSnap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        const machinesSnap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        const machines = scopeToSite(machinesSnap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        const fills = [];
        await Promise.all(tanks.map(async (tank) => {
          const snap = await getDocs(collection(db, 'organisations', orgId, 'dervTanks', tank.id, 'fills'));
          snap.docs.forEach(d => fills.push({ id: d.id, ...d.data() }));
        }));
        const byMachine = machines.map(m => {
          const mFills = fills.filter(f => f.machineId === m.id);
          const litres = mFills.reduce((sum, f) => sum + (f.litresAdded || 0), 0);
          const kgCO2 = +(litres * DEFRA_FACTOR).toFixed(1);
          return { ...m, litres, kgCO2, tCO2: +(kgCO2 / 1000).toFixed(3) };
        }).filter(m => m.litres > 0).sort((a, b) => b.kgCO2 - a.kgCO2);
        setData(byMachine);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const totalLitres = data.reduce((sum, m) => sum + m.litres, 0);
  const totalKg = data.reduce((sum, m) => sum + m.kgCO2, 0);
  const totalTonnes = +(totalKg / 1000).toFixed(2);

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Carbon emissions report"
          subtitle={'DEFRA standard · ' + DEFRA_FACTOR + 'kg CO₂ per litre of diesel'}
          actions={<Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>Export PDF</Button>}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total fuel consumed', value: totalLitres.toLocaleString() + 'L', colour: 'var(--color-text-primary)' },
            { label: 'Total CO₂ (kg)', value: totalKg.toLocaleString(), colour: 'var(--color-warning)' },
            { label: 'Total CO₂ (tonnes)', value: totalTonnes.toLocaleString(), colour: 'var(--color-danger)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
        {loading ? <LoadingSkeleton rows={5} card /> : data.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '40px' }}>
            <IconLeaf size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>No fuel data yet</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Log fuel fills to see carbon emissions</div>
          </Card>
        ) : (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Machine', 'Fuel consumed', 'CO₂ (kg)', 'CO₂ (tonnes)'].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {data.map((m, i) => (
              <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '11px 16px', borderBottom: i < data.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{m.makeLabel} {m.modelLabel}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{m.siteName}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{m.litres.toLocaleString()}L</div>
                <div style={{ fontSize: 13, color: 'var(--color-warning)' }}>{m.kgCO2.toLocaleString()}</div>
                <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>{m.tCO2}</div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}